import React, { useContext, useRef, useState, useEffect } from "react";
import { Alert } from "react-bootstrap";
import { Autocomplete, Box, Button, FormControl, TextField } from "@mui/material";
import gsap from "gsap";
import img from "../Images/img470.jpeg";
import { LanguageContext } from "./LanguageContext";

// ═══════════════════════════════════════
//  Falling Leaves Canvas
// ═══════════════════════════════════════
const FallingLeaves = ({ containerRef }) => {
  const canvasRef    = useRef(null);
  const animFrameRef = useRef(null);
  const leavesRef    = useRef([]);

  useEffect(() => {
    const canvas    = canvasRef.current;
    const container = containerRef?.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width  = container ? container.offsetWidth  : window.innerWidth;
      canvas.height = container ? container.offsetHeight : window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const colors = [
      "#4a7c3f", "#5a9e4a", "#3d6b35", "#6aad55",
      "#8B6914", "#a07830", "#c49a3c", "#7a5c1e",
    ];

    function shadeColor(hex, amount) {
      const num = parseInt(hex.replace("#", ""), 16);
      const r   = Math.max(0, Math.min(255, (num >> 16)         + amount));
      const g   = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
      const b   = Math.max(0, Math.min(255, (num & 0xff)        + amount));
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
    }

    const drawLeaf = (ctx, leaf) => {
      ctx.save();
      ctx.translate(leaf.x, leaf.y);
      ctx.rotate(leaf.rotation);
      ctx.scale(leaf.scaleX, leaf.scaleY);
      const s = leaf.size;
      ctx.beginPath();
      ctx.moveTo(0, -s);
      ctx.bezierCurveTo( s * 0.6, -s * 0.5,  s * 0.8,  s * 0.3, 0,  s);
      ctx.bezierCurveTo(-s * 0.8,  s * 0.3, -s * 0.6, -s * 0.5, 0, -s);
      ctx.closePath();
      const grad = ctx.createLinearGradient(-s * 0.5, -s, s * 0.5, s);
      grad.addColorStop(0,   leaf.color     + "ee");
      grad.addColorStop(0.5, leaf.color     + "cc");
      grad.addColorStop(1,   leaf.colorDark + "99");
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(0, -s);
      ctx.quadraticCurveTo(s * 0.1, 0, 0, s);
      ctx.strokeStyle = leaf.colorDark + "88";
      ctx.lineWidth   = s * 0.06;
      ctx.stroke();
      ctx.restore();
    };

    const GROUND_Y = () => canvas.height - 18;

    const createLeaf = (startFromTop = true) => {
      const color = colors[Math.floor(Math.random() * colors.length)];
      return {
        x:            Math.random() * canvas.width,
        y:            startFromTop ? -30 - Math.random() * 120 : Math.random() * canvas.height,
        size:         8 + Math.random() * 12,
        speedY:       0.5 + Math.random() * 1,
        speedX:       (Math.random() - 0.5) * 0.7,
        rotation:     Math.random() * Math.PI * 2,
        rotSpeed:     (Math.random() - 0.5) * 0.04,
        scaleX:       1, scaleY: 1,
        scaleDir:     Math.random() > 0.5 ? 1 : -1,
        scaleSpeed:   0.01 + Math.random() * 0.02,
        swingAmp:     0.3  + Math.random() * 0.5,
        swingSpeed:   0.01 + Math.random() * 0.02,
        swingOffset:  Math.random() * Math.PI * 2,
        color,
        colorDark:    shadeColor(color, -40),
        opacity:      0.5 + Math.random() * 0.3,
        time:         0,
        state:        "falling",
        restTimer:    0,
        restDuration: 120 + Math.floor(Math.random() * 200),
      };
    };

    leavesRef.current = Array.from({ length: 10 }, () => createLeaf(false));
    let frameCount = 0;

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frameCount++;
      if (frameCount % 110 === 0) leavesRef.current.push(createLeaf(true));

      leavesRef.current = leavesRef.current.filter((leaf) => {
        leaf.time++;
        const ground = GROUND_Y();
        if (leaf.state === "falling") {
          leaf.y        += leaf.speedY;
          leaf.x        += leaf.speedX + Math.sin(leaf.time * leaf.swingSpeed + leaf.swingOffset) * leaf.swingAmp;
          leaf.rotation += leaf.rotSpeed;
          leaf.scaleX   += leaf.scaleDir * leaf.scaleSpeed;
          if (Math.abs(leaf.scaleX) >= 1) leaf.scaleDir *= -1;
          if (leaf.y > ground - 80) {
            const t     = Math.max(0, (leaf.y - (ground - 80)) / 80);
            leaf.speedY = Math.max(0.05, leaf.speedY * (1 - t * 0.05));
            leaf.speedX *= 0.98; leaf.rotSpeed *= 0.97;
          }
          if (leaf.y >= ground) {
            leaf.y = ground; leaf.speedY = 0;
            leaf.speedX = 0; leaf.rotSpeed = 0;
            leaf.state = "resting";
          }
        } else if (leaf.state === "resting") {
          leaf.restTimer++;
          if (leaf.restTimer >= leaf.restDuration) leaf.state = "fading";
        } else if (leaf.state === "fading") {
          leaf.opacity -= 0.008;
          if (leaf.opacity <= 0) return false;
        }
        ctx.globalAlpha = Math.max(0, leaf.opacity);
        drawLeaf(ctx, leaf);
        ctx.globalAlpha = 1;
        return true;
      });
    };

    animate();
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas ref={canvasRef} style={{
      position: "absolute", inset: 0,
      zIndex: 5, pointerEvents: "none",
    }} />
  );
};

// ═══════════════════════════════════════
//  Countries
// ═══════════════════════════════════════
const countries = [
  { code: "AE", label: "United Arab Emirates", phone: "971" },
  { code: "SA", label: "Saudi Arabia",         phone: "966" },
  { code: "KW", label: "Kuwait",               phone: "965" },
  { code: "QA", label: "Qatar",                phone: "974" },
  { code: "BH", label: "Bahrain",              phone: "973" },
  { code: "OM", label: "Oman",                 phone: "968" },
  { code: "EG", label: "Egypt",                phone: "20"  },
  { code: "JO", label: "Jordan",               phone: "962" },
  { code: "LB", label: "Lebanon",              phone: "961" },
  { code: "IQ", label: "Iraq",                 phone: "964" },
  { code: "SY", label: "Syria",                phone: "963" },
  { code: "MA", label: "Morocco",              phone: "212" },
  { code: "GB", label: "United Kingdom",       phone: "44"  },
  { code: "US", label: "United States",        phone: "1"   },
  { code: "FR", label: "France",               phone: "33"  },
  { code: "DE", label: "Germany",              phone: "49"  },
  { code: "IN", label: "India",                phone: "91"  },
  { code: "PK", label: "Pakistan",             phone: "92"  },
  { code: "RU", label: "Russian Federation",   phone: "7"   },
  { code: "CN", label: "China",                phone: "86"  },
  { code: "AU", label: "Australia",            phone: "61"  },
  { code: "CA", label: "Canada",               phone: "1"   },
];

// ✅ الـ API URL الجديد
const API_URL =
  "https://homewalkers.realatixcrm.com/api/v1/users/create-with-params" +
  "?addedby=68da45a5b37ddfab3b73bd8b" +
  "&stage=68d110bbad5a0732ad44e5cf" +
  "&Channal=6a48bfa6a4cf5854ad69d511" +
  "&project=68e36f383b0e6ff7d205ded6" +
  "&salesdata=68da75b5b37ddfab3b7463f5" +
  "&Communicationway=69159308820816f4ad71c3bf" +
  "&campaignid=69ca62736bc129db99a826fb" +
  "&includePhone2=false" +
  "&includeWhatsapp=false" +
  "&includeJobDesc=false" +
  "&includeQuestion1=false" +
  "&includeQuestion2=false" +
  "&includeQuestion3=false" +
  "&includeQuestion4=false" +
  "&includeQuestion5=false" +
  "&campaignRedirectLink=https%3A%2F%2Fmassar3-d09ef.web.app%2F";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    background:   "rgba(255,255,255,0.12)",
    color:        "#fff",
    fontFamily:   "'Cairo', sans-serif",
    "& fieldset":             { borderColor: "rgba(255,255,255,0.3)"  },
    "&:hover fieldset":       { borderColor: "rgba(255,255,255,0.6)"  },
    "&.Mui-focused fieldset": { borderColor: "#6aad55"                },
  },
  "& .MuiInputLabel-root": {
    color:      "rgba(255,255,255,0.7)",
    fontFamily: "'Cairo', sans-serif",
    "&.Mui-focused": { color: "#6aad55" },
  },
  "& input":            { color: "#fff", fontFamily: "'Cairo', sans-serif" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" },
};

// ═══════════════════════════════════════
//  Validation
// ═══════════════════════════════════════
// const validate = (formData, isArabic) => {
//   const { name, email, country, phone } = formData;

//   // ✅ الاسم — مش فاضي ومش أقل من حرفين
//   if (!name.trim())
//     return isArabic ? "الاسم مطلوب" : "Name is required";
//   if (name.trim().length < 2)
//     return isArabic ? "الاسم يجب أن يكون حرفين على الأقل" : "Name must be at least 2 characters";

//   // ✅ الإيميل
//   if (!email.trim())
//     return isArabic ? "البريد الإلكتروني مطلوب" : "Email is required";
//   if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
//     return isArabic ? "البريد الإلكتروني غير صالح" : "Invalid email address";

//   // ✅ الدولة
//   if (!country)
//     return isArabic ? "يرجى اختيار الدولة" : "Please select a country";

//   // ✅ الهاتف — نشيل الكود ونتحقق إن في أرقام كافية
//   const codeStr     = `+${country.phone}`;
//   const digitsOnly  = phone.replace(codeStr, "").replace(/\D/g, "");
//   if (!phone || phone === codeStr)
//     return isArabic ? "رقم الهاتف مطلوب" : "Phone number is required";
//   if (digitsOnly.length < 7)
//     return isArabic ? "رقم الهاتف يجب أن يحتوي على 7 أرقام على الأقل" : "Phone must be at least 7 digits";
//   if (digitsOnly.length > 12)
//     return isArabic ? "رقم الهاتف طويل جداً" : "Phone number is too long";

//   return null; // ✅ كل حاجة تمام
// };

// ═══════════════════════════════════════
//  ContactForm
// ═══════════════════════════════════════
const ContactForm = () => {
  const { language } = useContext(LanguageContext);
  const isArabic = language === "ar";

  const sectionRef = useRef(null);
  const formRef    = useRef(null);

  const [formData, setFormData] = useState({
    name: "", email: "",
    country: countries[0],
    phone: "+971",
  });

  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);
  const [alert,   setAlert]   = useState({ show: false, message: "", variant: "" });

  // ── Form entrance animation ──
  useEffect(() => {
    const section = sectionRef.current;
    const form    = formRef.current;
    if (!section || !form) return;
    gsap.set(form, { opacity: 0, y: 50 });
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting)
          gsap.to(form, { opacity: 1, y: 0, duration: 0.9, ease: "power3.out" });
        else
          gsap.set(form, { opacity: 0, y: 50 });
      });
    }, { threshold: 0.15 });
    obs.observe(section);
    return () => obs.disconnect();
  }, [language]);

  const handleChange = (field) => (e) => {
    setFormData((p) => ({ ...p, [field]: e.target.value }));
    // ✅ امسح الـ error لما يبدأ يكتب
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleCountryChange = (_, val) => {
    const c = val || countries[0];
    setFormData((p) => ({ ...p, country: c, phone: `+${c.phone}` }));
    if (errors.country) setErrors((p) => ({ ...p, country: "" }));
  };

  const handlePhoneChange = (e) => {
    const code = `+${formData.country.phone}`;
    const val  = e.target.value;
    setFormData((p) => ({
      ...p,
      phone: val.startsWith(code) ? val : code + val.replace(/^\+\d+/, ""),
    }));
    if (errors.phone) setErrors((p) => ({ ...p, phone: "" }));
  };

// ✅ امسح الـ validate function القديمة خالص

// ─── handleSubmit ─────────────────────────────────────────────
const handleSubmit = async (e) => {
  e.preventDefault();

  const { name, email, country, phone } = formData;

  // ✅ الدولة لازم تتاختار
  if (!country)
    return setAlert({ show: true, message: isArabic ? "يرجى اختيار الدولة" : "Please select a country", variant: "danger" });

  // ✅ الهاتف بس
  const codeStr    = `+${country.phone}`;
  const digitsOnly = phone.replace(codeStr, "").replace(/\D/g, "");
  if (!phone || phone === codeStr)
    return setAlert({ show: true, message: isArabic ? "رقم الهاتف مطلوب" : "Phone number is required", variant: "danger" });
  if (digitsOnly.length < 7)
    return setAlert({ show: true, message: isArabic ? "رقم الهاتف يجب أن يحتوي على 7 أرقام على الأقل" : "Phone must be at least 7 digits", variant: "danger" });
  if (digitsOnly.length > 12)
    return setAlert({ show: true, message: isArabic ? "رقم الهاتف طويل جداً" : "Phone number is too long", variant: "danger" });

  setLoading(true);
  setAlert({ show: false, message: "", variant: "" });

  try {
    const res = await fetch(API_URL, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        // ✅ لو الاسم فاضي نحط "no name"
        name:    name.trim() || "no name",
        // ✅ الإيميل بدون validate
        email:   email.trim().toLowerCase() || "",
        country: country.label,
        phone:   phone,
      }),
    });

    if (!res.ok) throw new Error(`${res.status}`);

    setAlert({
      show: true,
      message: isArabic ? "تم الإرسال بنجاح ✅" : "Submitted successfully ✅",
      variant: "success",
    });

    setFormData({ name: "", email: "", country: countries[0], phone: "+971" });
    setErrors({});

  } catch (err) {
    const status = err.message;
    setAlert({
      show: true,
      message:
        status.includes("409") ? (isArabic ? "رقم الهاتف مسجّل مسبقاً" : "Phone already registered")
      : status.includes("400") ? (isArabic ? "بيانات غير صحيحة، يرجى المراجعة" : "Invalid data, please review")
      : (isArabic ? "حدث خطأ أثناء الإرسال، حاول مرة أخرى" : "Submission error, please try again"),
      variant: "danger",
    });
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (!alert.show) return;
    const t = setTimeout(() => setAlert((p) => ({ ...p, show: false })), 5000);
    return () => clearTimeout(t);
  }, [alert.show]);

  return (
    <Box
      id="contact"
      ref={sectionRef}
      dir={isArabic ? "rtl" : "ltr"}
      sx={{
        position:       "relative",
        display:        "flex",
        justifyContent: "center",
        alignItems:     "center",
        minHeight:      "100vh",
        background:     `url(${img}) center/cover no-repeat`,
        padding:        { xs: "2rem 1rem", sm: "3rem 2rem" },
        fontFamily:     "'Cairo', sans-serif",
        overflow:       "hidden",
      }}
    >
      {/* Dark overlay */}
      <Box sx={{
        position: "absolute", inset: 0,
        background: "linear-gradient(160deg, rgba(10,30,10,0.75) 0%, rgba(15,40,15,0.62) 100%)",
        zIndex: 0,
      }} />

      {/* Falling Leaves */}
      <FallingLeaves containerRef={sectionRef} />

      {/* Alert */}
      {alert.show && (
        <Box sx={{
          position: "absolute", top: 24, left: "50%",
          transform: "translateX(-50%)",
          width: "90%", maxWidth: "480px", zIndex: 20,
        }}>
          <Alert
            variant={alert.variant}
            onClose={() => setAlert((p) => ({ ...p, show: false }))}
            dismissible
          >
            {alert.message}
          </Alert>
        </Box>
      )}

      {/* Form card */}
      <Box
        ref={formRef}
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          position:       "relative",
          zIndex:         10,
          width:          "100%",
          maxWidth:       { xs: "100%", sm: "560px" },
          background:     "rgba(255,255,255,0.08)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          border:         "1px solid rgba(255,255,255,0.18)",
          borderRadius:   "24px",
          padding:        { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
          boxShadow:      "0 24px 64px rgba(0,0,0,0.4)",
          display:        "flex",
          flexDirection:  "column",
          gap:            "1.2rem",
          opacity:        0,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 1 }}>
          <Box sx={{
            display: "inline-block",
            background: "linear-gradient(135deg, #4a7c3f, #6aad55)",
            borderRadius: "30px",
            px: 2.5, py: 0.6,
            fontSize: "0.72rem", fontWeight: 700,
            letterSpacing: "0.15em", textTransform: "uppercase",
            color: "#fff", mb: 1.5,
            fontFamily: "'Cairo', sans-serif",
          }}>
            🌿 {isArabic ? "تواصل معنا" : "Get in Touch"}
          </Box>

          <Box component="h2" sx={{
            fontFamily: "'Cairo', sans-serif", fontWeight: 900,
            fontSize: { xs: "1.6rem", sm: "2rem" },
            color: "#fff", lineHeight: 1.2, mb: 0.5, mt: 0,
          }}>
            {isArabic ? "للمزيد من التفاصيل" : "For More Details"}
          </Box>

          <Box component="p" sx={{
            fontFamily: "'Cairo', sans-serif", fontSize: "0.92rem",
            color: "rgba(255,255,255,0.65)", m: 0,
          }}>
            {isArabic ? "سجّل بياناتك وسنتواصل معك قريباً" : "Register your info and we'll reach out soon"}
          </Box>
        </Box>

        <Box sx={{ height: "1px", background: "rgba(255,255,255,0.12)", mx: -1 }} />

        {/* Name */}
        <TextField
          label={isArabic ? "الاسم الكامل *" : "Full Name *"}
          fullWidth
          value={formData.name}
          onChange={handleChange("name")}
          inputProps={{ dir: isArabic ? "rtl" : "ltr" }}
          sx={fieldSx}
        />

        {/* Email */}
        <TextField
          label={isArabic ? "البريد الإلكتروني *" : "Email Address *"}
          type="email" fullWidth
          value={formData.email}
          onChange={handleChange("email")}
          inputProps={{ dir: "ltr" }}
          sx={fieldSx}
        />

        {/* Country + Phone */}
        <FormControl fullWidth>
          <Box sx={{ display: "flex", gap: 1.5, flexDirection: { xs: "column", sm: "row" } }}>
            <Autocomplete
              options={countries}
              value={formData.country}
              onChange={handleCountryChange}
              getOptionLabel={(o) => o.label}
              renderOption={(props, option) => (
                <Box component="li" {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: "'Cairo', sans-serif" }}>
                  <img loading="lazy" width="20"
                    src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                    alt=""
                  />
                  {option.label} (+{option.phone})
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isArabic ? "الدولة *" : "Country *"}
                  sx={fieldSx}
                />
              )}
              sx={{
                flex: 1.2,
                "& .MuiAutocomplete-popupIndicator": { color: "rgba(255,255,255,0.6)" },
                "& .MuiAutocomplete-clearIndicator": { color: "rgba(255,255,255,0.6)" },
              }}
            />
            <TextField
              label={isArabic ? "رقم الهاتف *" : "Phone *"}
              type="tel"
              value={formData.phone}
              onChange={handlePhoneChange}
              sx={{ ...fieldSx, flex: 1 }}
              inputProps={{ dir: "ltr" }}
            />
          </Box>
        </FormControl>

        {/* Submit */}
        <Button
          type="submit"
          fullWidth
          disabled={loading}
          sx={{
            mt: 0.5,
            height:     { xs: "48px", sm: "54px" },
            borderRadius: "14px",
            fontSize:   { xs: "0.95rem", sm: "1rem" },
            fontWeight: 700,
            fontFamily: "'Cairo', sans-serif",
            background: loading
              ? "rgba(74,124,63,0.5)"
              : "linear-gradient(135deg, #4a7c3f, #6aad55)",
            color:      "#fff",
            textTransform: "none",
            boxShadow:  "0 8px 24px rgba(74,124,63,0.4)",
            transition: "all 0.25s ease",
            "&:hover": {
              background:  "linear-gradient(135deg, #3d6b34, #5a9a47)",
              boxShadow:   "0 12px 32px rgba(74,124,63,0.5)",
              transform:   "translateY(-2px)",
            },
          }}
        >
          {loading
            ? (isArabic ? "جاري الإرسال..." : "Submitting...")
            : (isArabic ? "إرسال الطلب" : "Submit Request")
          }
        </Button>
      </Box>
    </Box>
  );
};

export default ContactForm;